import Order, { OrderArticle } from './model/Order';
import sortBy from 'lodash.sortby';
import React, { useCallback, useEffect, useState } from 'react';
import Loader from '../../loader/Loader';
import { useOrderQueries } from './hooks/useOrderQueries';

const ORDER_SELECTOR_OPTION_SELECTOR_ITEM_SHOW_PICTURES_COUNT: number = 5;

const sortArticles = (articles: OrderArticle[]) => sortBy(articles, ['id']);

const arrangeArticlesForDisplaying = (articles: OrderArticle[]): ArticlesForDisplaying => {
  const articlesToDisplay = sortArticles(articles)
    .filter((article) => article.valid)
    .slice(0, ORDER_SELECTOR_OPTION_SELECTOR_ITEM_SHOW_PICTURES_COUNT);
  return {
    articles: articlesToDisplay,
    additionalArticlesCount: articles.length - articlesToDisplay.length,
  };
};

type OrderArticlesPicturesProps = {
  order: Order;
};

type ArticlesForDisplaying = { articles: OrderArticle[]; additionalArticlesCount: number };

export const OrderArticlesPictures = ({ order }: OrderArticlesPicturesProps) => {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [articles, setArticles] = useState<OrderArticle[]>([new OrderArticle()]);
  const [articlesToDisplay, setArticlesToDisplay] = useState<ArticlesForDisplaying>({
    articles: [],
    additionalArticlesCount: 0,
  });

  const { getOrderArticles } = useOrderQueries();

  useEffect(() => {
    setIsLoaded(false);
    getOrderArticles(order.orderId, order.suborderId)
      .then((orderArticles) => {
        setArticles(orderArticles);
        setIsLoaded(true);
      })
      .catch((e) => {
        console.warn('Error when fetching order articles', e);
        onImagesLoadError();
      });
  }, [order.orderId, order.suborderId]);

  useEffect(() => {
    setArticlesToDisplay(arrangeArticlesForDisplaying(articles));
  }, [articles]);

  const onImagesLoadError = useCallback(() => {
    articles.forEach((article) => onImageLoadError(article));
  }, [articles]);

  const onImageLoadError = useCallback(
    (article: OrderArticle) => {
      console.log(
        `Problem with loading image for article "${article.name}"(${article.id}) from url "${article.pictureUrl}"`,
      );
      const articleForChange = articles.find((a) => a.id === article.id);
      if (articleForChange) {
        articleForChange.valid = false;
      }
      setArticles((prevState) => [...prevState]);
      setIsLoaded(true);
    },
    [articles],
  );

  return (
    <div
      className={'orderSelector__optionSelectorItemPictures'}
      data-test="orderSelector.optionSelector.list.pictures">
      <Loader isLoading={!isLoaded} isSimple size={24}>
        {articlesToDisplay.articles.map((article) => (
          <div className={'orderSelector__optionSelectorItemPicture'}>
            <img
              src={article.pictureUrl}
              alt={article.name}
              title={article.name}
              onError={() => onImageLoadError(article)}
            />
          </div>
        ))}
      </Loader>
      {articlesToDisplay.additionalArticlesCount > 0 && (
        <div data-test="orderSelector.optionSelector.list.pictures.additionalArticles">
          <strong className="z-info-tag--neutral">
            +{articlesToDisplay.additionalArticlesCount}
          </strong>
        </div>
      )}
    </div>
  );
};
